import type { AgentBriefInput, CreditRange, MessageRating } from '@aikka/types';
import { API_BASE_URL, apiClient, getAuthToken, refreshRejectedAuthToken } from './client';

// ─── Erreurs ─────────────────────────────────────────────

/**
 * Un refus du back sur une route d'agent, avec le CODE et pas seulement la phrase.
 *
 * Les deux routes streamées lisaient le corps d'un `!res.ok` avec `res.text()` et
 * le jetaient tel quel dans `new Error(...)` : une réponse d'erreur de Nest est du
 * JSON, donc l'opérateur voyait littéralement
 * `{"statusCode":403,"error":"Forbidden","code":"email_not_confirmed",...}`
 * s'afficher sous sa question. Le corps est maintenant lu comme ce qu'il est, et
 * le `code` survit jusqu'à l'écran — c'est lui, et jamais le texte du message, qui
 * décide si l'on propose de renvoyer le courrier de confirmation (l'app parle
 * trois langues : reconnaître une phrase anglaise ne marcherait qu'ici).
 *
 * Le message reste rempli quoi qu'il arrive, y compris quand le corps n'est pas du
 * JSON : une erreur sans phrase n'aide personne.
 */
export class AgentRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly email?: string,
  ) {
    super(message);
    this.name = 'AgentRequestError';
  }
}

/** Le code métier que porte un refus, quand l'appelant en a un. */
export function agentErrorCode(err: unknown): string | undefined {
  return err instanceof AgentRequestError ? err.code : undefined;
}

/**
 * Transforme une réponse HTTP en échec en `AgentRequestError`. Jamais elle-même
 * en échec : un corps illisible redevient le texte brut, et un corps vide le
 * statut.
 */
async function agentErrorFromResponse(res: Response): Promise<AgentRequestError> {
  const raw = await res.text().catch(() => '');
  try {
    const body = JSON.parse(raw) as { message?: unknown; code?: unknown; email?: unknown };
    const message =
      typeof body.message === 'string'
        ? body.message
        : Array.isArray(body.message)
          ? body.message.join(', ')
          : raw || `HTTP ${res.status}`;
    return new AgentRequestError(
      message,
      res.status,
      typeof body.code === 'string' ? body.code : undefined,
      typeof body.email === 'string' ? body.email : undefined,
    );
  } catch {
    return new AgentRequestError(raw || `HTTP ${res.status}`, res.status);
  }
}

// ─── Types ───────────────────────────────────────────────

/**
 * An agent as `GET /agent/agents` returns it.
 *
 * `skills[].credits` — a firm per-skill point — is GONE, and its absence is the
 * change: the debit has followed real gateway cost since the câblage commit, so a
 * point here was a price the server no longer promises. What a skill carries now is
 * the announced fourchette, derived by the same `creditRange()` the pricing endpoint
 * and the brand-plan screens use, plus `flatEstimate` — the forfait still held against
 * the balance before the turn runs, which is what explains a refusal a band's low end
 * would otherwise contradict.
 *
 * `creditRange` is nullable by the derivation's own contract: a skill with no usable
 * forfait announces nothing rather than a fabricated band. Any surface rendering one
 * must also say, in words, that it is an estimate and not a quote.
 */
export interface Agent {
  id: string;
  name: string;
  displayName: string;
  avatar: string;
  skills: {
    id: string;
    name: string;
    description: string;
    creditRange: CreditRange | null;
  }[];
  /** Stated by the server on the payload, so a band cannot be mistaken for a quote. */
  creditsAreEstimates: boolean;
}

export interface Conversation {
  id: string;
  agent: 'AIKKA' | 'MEDICAL' | 'MARKETING';
  title: string | null;
  isArchived: boolean;
  /** Pinned conversations float to the top of the sidebar. */
  isPinned: boolean;
  /**
   * Durable run status (conversation-continuity). `running` while a run is in
   * flight server-side — this is what lights the "en cours" spinner in the
   * sidebar and tells `loadConversation` to reconnect to a live run. Settled or
   * absent otherwise; `null` on conversations that never ran a streamed turn.
   */
  runStatus?: 'running' | 'completed' | 'error' | null;
  createdAt: string;
  updatedAt: string;
  messages?: Message[];
}

export interface ConversationPreview extends Omit<Conversation, 'messages'> {
  messages: { content: string; role: string; createdAt: string }[];
  /**
   * Read-state (claude.ai-style unread dot). `true` when the conversation was
   * never opened or when new activity landed since the last open — the orange
   * dot shows on these rows and clears once the user opens the conversation
   * (which PATCHes `lastReadAt`). Distinct from the `runStatus` spinner: the
   * spinner means a run is live, the dot means "content you have not read yet".
   */
  unread?: boolean;
  /** When the user last opened this conversation (marks it read). Null if never. */
  lastReadAt?: string | null;
}

export interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolCalls?: unknown;
  toolResults?: unknown;
  citations?: Citation[];
  files?: GeneratedFile[];
  /**
   * Every insight card streamed during this turn, as the server persisted it —
   * charts included. A reopened conversation reads its inline charts back from
   * here. Absent on rows that carried none.
   */
  insightCards?: unknown;
  creditsUsed: number;
  model?: string;
  /**
   * Conversation-continuity: set only on an assistant message the boot reconciler
   * materialised from a run orphaned by a store-back restart (reason
   * `server_restart`). `loadConversation` renders such a message's content with a
   * terminal "interrupted" note + Retry, rather than as a completed answer. Null
   * / absent on every ordinary message.
   */
  interruptedReason?: string | null;
  /**
   * The agent run that produced this assistant turn (`RunTrace.runId`). Stamped by
   * the streaming path; absent on user rows, on the non-streaming path and on rows
   * written before ratings existed. It is what makes a rating readable: the trace
   * on the other end carries the prompt and the tool calls of the rated answer.
   */
  runId?: string | null;
  /**
   * The operator's own rating of THIS answer, if they gave one. Rides along with
   * the transcript so reopening a conversation shows the thumbs already given
   * instead of offering to rate the same answer twice. Null / absent = not rated:
   * there is no third rating value.
   */
  feedback?: MessageFeedback | null;
  createdAt: string;
}

/** One stored rating, as the transcript and the rate endpoint both return it. */
export interface MessageFeedback {
  rating: MessageRating;
  comment: string | null;
  /** The run the rating is joinable to, when the rated answer carries one. */
  runId?: string | null;
}

export interface Citation {
  url: string;
  title: string;
  /**
   * The excerpt that justifies the claim. Persisted on every streamed assistant
   * turn (the raw upstream result is stored, so this is NOT length-bounded here
   * — consumers clip it). Absent on rows written before the evidence seam, and
   * on the annotation-shaped citations of the non-streaming path.
   */
  snippet?: string;
  /** Publication date as upstream reported it. Absent when unknown. */
  date?: string;
}

export interface ExcelSheetPreview {
  name?: string;
  headers?: string[];
  rows?: unknown[][];
}

export interface SlidePreview {
  title?: string;
  content?: string;
}

export interface GeneratedFile {
  type: 'excel' | 'pptx' | 'html';
  title: string;
  download_url?: string;
  data?: unknown;
  sheets?: ExcelSheetPreview[];
  slides?: SlidePreview[];
  html?: string;
  chart_type?: string;
}

export interface UploadedFile {
  id: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  hasText: boolean;
}

export interface CreditBalance {
  balance: number;
  total: number;
  used: number;
  plan: string;
}

/** One usage "scope" row — a real dimension the backend tracks (per-agent). */
export interface UsageScopeUsage {
  scope: string;
  creditsUsed: number;
  percentOfQuota: number;
}

/** Sentinel model bucket for consumption booked before per-model attribution
 *  existed (or spend with no gateway model). Matches the backend sentinel. */
export const UNKNOWN_MODEL_BUCKET = 'unknown';

/** One usage row broken down per LLM gateway model. Rows with no recorded model
 *  collapse into `UNKNOWN_MODEL_BUCKET` (honest, never re-attributed). */
export interface UsageModelUsage {
  /** Gateway model id (e.g. 'anthropic/claude-sonnet-4-6'), or `UNKNOWN_MODEL_BUCKET`. */
  model: string;
  creditsUsed: number;
  percentOfQuota: number;
}

/**
 * Honest usage summary (GET /agent/usage). Every figure is real: the plan quota,
 * the consumed counter, and the current-week consumption ledger. Where the
 * backend has no contract (a weekly cap, per-model tracking, a usage-credits
 * opt-in), the shape says so rather than surfacing a fabricated number.
 */
export interface UsageSummary {
  plan: string;
  updatedAt: string;
  session: {
    creditsTotal: number;
    creditsUsed: number;
    creditsRemaining: number;
    percentUsed: number;
    resetAt: string;
  };
  weekly: {
    period: string;
    periodStart: string;
    periodEnd: string;
    creditsUsed: number;
    quota: number;
    percentOfQuota: number;
    hasWeeklyQuota: boolean;
    byScope: UsageScopeUsage[];
    /** Per-LLM-model breakdown (claude.ai parity). Legacy consumption with no
     *  recorded model shows under the `UNKNOWN_MODEL_BUCKET` row. */
    byModel: UsageModelUsage[];
  };
  usageCredits: {
    supported: boolean;
    enabled: boolean;
  };
}

/** SSE payloads from POST /agent/:id/chat/stream */
export type AgentStreamEvent =
  | { type: 'conversation_id'; data: { conversationId: string } }
  | { type: 'text_delta'; data: { delta: string } }
  | { type: 'text_done'; data: { text?: string } }
  | { type: 'replace'; data: { text: string } }
  | { type: 'reasoning_started' | 'reasoning_stopped'; data: { thought?: string } }
  | { type: 'search_queries'; data: { queries: string[] } }
  | { type: 'search_results'; data: { results: unknown[] } }
  // `callId` pairs this line with its `tool_call_done`. Optional: a store-back
  // that predates call-id pairing omits it, and the UI falls back to the name.
  | { type: 'tool_call_started'; data: { name: string; callId?: string } }
  | { type: 'tool_call_done'; data: Record<string, unknown> }
  | { type: 'file_generated'; data: GeneratedFile }
  // Structured finding card (store-back `QualityInsight`). Shape is open on
  // purpose — `kind` selects the card body and the fields it needs ride along.
  | { type: 'insight'; data: Record<string, unknown> }
  | {
      type: 'done';
      data: {
        messageId?: string;
        conversationId?: string;
        // The run behind the answer that just settled, so the live client can rate
        // it straight away instead of waiting for a reload to learn the link.
        runId?: string;
        creditsUsed?: number;
        usage?: unknown;
      };
    }
  | { type: 'error'; data: { message?: string } }
  | { type: 'credits_deducted'; data: Record<string, unknown> };

/**
 * Extra SSE markers only the reconnect stream (`GET /agent/conversations/:id/live`)
 * emits, on top of the ordinary {@link AgentStreamEvent} vocabulary it replays.
 *
 *  • `reconnected` — the opening frame (SSE id 0): the run's durable status, its
 *    latest event id, and whether the replay is `truncated` (the client's offset
 *    fell off the server ring buffer, so it must resync from persisted messages
 *    rather than trust a partial tail).
 *  • `no_active_run` — nothing is live for the conversation (finished, evicted,
 *    or lost to a restart); the client falls back to the persisted transcript.
 */
export type AgentReconnectEvent =
  | AgentStreamEvent
  // Brand-plan runs are durable runs like any other, watched through this same
  // route — so their vocabulary belongs in it rather than in a parallel transport.
  | BrandPlanRunEvent
  | {
      type: 'reconnected';
      data: {
        conversationId: string;
        runId: string;
        status: 'running' | 'completed' | 'error';
        lastEventId: number;
        truncated: boolean;
      };
    }
  | { type: 'no_active_run'; data: { conversationId: string } };

// ─── Brand plan (MKT-03) ──────────────────────────────────
//
// The three-phase method, as the server actually exposes it:
//   Phase 1  POST /agent/brand-plan/preliminary  — web-grounded evidence sweep,
//            then the guided question set built from what it found;
//   Phase 2  answered by the operator, client-side (the questions come back on the
//            stream; the answers travel to Phase 3);
//   Phase 3  POST /agent/brand-plan/generate     — spine + twelve sections.
//
// BOTH long routes are 202-ACCEPTED, not results: they hand back a
// {@link BrandPlanRunHandle} and the work continues server-side as a durable run
// on the conversation. What comes back over the wire arrives on the EXISTING
// reconnect stream — {@link streamAgentReconnect} — as the `brand_plan_*` events
// below, so there is no second transport and the operator can close the tab.
//
// The client is STATELESS between phases by server design: Phase 3 replays the
// Phase 1 {@link BrandPlanResearch} it received. A caller must therefore keep that
// object (it is also persisted on the conversation, so a reload can recover it).

/** One entry of the deterministic outline from `GET /agent/brand-plan/sections`. */
export interface BrandPlanSectionSpec {
  key: string;
  title: string;
}

/** A source the pipeline actually read. Never fabricated server-side. */
export interface BrandPlanProvenance {
  source: string;
  url: string;
  title?: string;
}

/**
 * A DECLARED WEAKNESS in the output — the load-bearing honesty primitive of this
 * pipeline. The server never throws on thin evidence: it degrades and flags. Flags
 * ride on Phase 1 (`BrandPlanResearch.confidenceFlags`, from buckets that returned
 * nothing) and on Phase 3 (`BrandPlanOutput.confidenceFlags`, per section), and
 * every surface that renders a plan MUST render them with it. A degraded plan shown
 * as a complete one is the worst outcome this feature can produce, so these are
 * typed as first-class data and never widened away.
 */
export interface BrandPlanConfidenceFlag {
  /** Which section (or research bucket) the gap belongs to. */
  section: string;
  /** What is missing or thin. */
  gap: string;
  /** What the operator can do to close it. */
  suggestion: string;
}

/** One of the seven Phase-1 evidence buckets. `ok:false` ⇒ the sweep found nothing usable. */
export interface BrandPlanResearchBucket {
  key: string;
  ok: boolean;
  summary: string;
  sources: BrandPlanProvenance[];
  fetchedAt: string;
  /** Why this bucket failed, when it did. */
  error?: string;
}

/** The full context Phase 1 researched against, and Phase 3 writes against. */
export interface BrandPlanContext {
  brand: string;
  molecule?: string;
  indication: string;
  markets: string[];
  competitors: string[];
  ownedDomains: string[];
  languages?: string[];
  personaHints?: string[];
  budgetTier?: 'S' | 'M' | 'L';
  offlimitChannels?: string[];
  priorityChannels?: string[];
  ambition?: string;
}

/**
 * The Phase 1 output. The caller MUST hold on to this: Phase 3 is stateless and
 * takes it back in the request body (`GenerateBrandPlanPayload.research`).
 */
export interface BrandPlanResearch {
  ctx: BrandPlanContext;
  buckets: BrandPlanResearchBucket[];
  confidenceFlags: BrandPlanConfidenceFlag[];
  provenance: BrandPlanProvenance[];
  generatedAt: string;
}

export type BrandPlanQuestionBlock =
  | 'ambition'
  | 'market'
  | 'objectives'
  | 'personas'
  | 'value_prop'
  | 'tactics';

/**
 * One guided question. It is not a bare prompt: it carries what the research found
 * (`contextSummary`), what Aikka would answer (`recommendation`) and why it matters
 * here (`insight`), so the operator can accept, adjust or override on evidence.
 */
export interface BrandPlanQuestion {
  id: string;
  block: BrandPlanQuestionBlock;
  question: string;
  contextSummary: string;
  recommendation: string;
  insight: string;
  type: 'text' | 'select' | 'multi' | 'textarea' | 'number';
  options?: { label: string; description: string; sourceUrl?: string }[];
  suggestedAnswer?: string;
}

/** Phase-2 answers, keyed by {@link BrandPlanQuestion.id}. */
export type BrandPlanAnswers = Record<string, string | string[] | number>;

export interface BrandPlanSection {
  key: string;
  title: string;
  markdown: string;
  sourcesUsed: string[];
}

/** How one section failed to use the spine it was handed. */
export interface BrandPlanCoverageFinding {
  section: string;
  defect: string;
  idKind?: string;
  detail: string[];
}

/** The deterministic used-before-defined pass over the finished plan. */
export interface BrandPlanChainAudit {
  /** Chain IDs a section cited that the spine never defined. Should be 0. */
  unknownIds: string[];
  /** Spine IDs no section used — a committed choice the plan never spent. */
  unusedIds: string[];
  bySection: Record<string, string[]>;
  coverage: BrandPlanCoverageFinding[];
}

/** `covered` = every section used its slice; `incomplete` = chained but a section ignored it;
 *  `unchained` = there is no spine at all. Three states a reader must be able to tell apart. */
export type BrandPlanChainState = 'covered' | 'incomplete' | 'unchained';

/** Counted facts about the produced plan — all real, none interpolated. */
export interface BrandPlanMetrics {
  sectionsGenerated: number;
  signalsUsed: number;
  uniqueSources: number;
  confidenceComposite: number;
  chainSpineOk: boolean;
  chainIdsUnknown: number;
  chainState: BrandPlanChainState;
  chainSectionsUncovered: number;
  chainCoverageDefects: number;
  chainLeveragePointsUnsourced: number;
  chainBindingsMismatched: number;
}

export interface BrandPlanOutput {
  markdown: string;
  executiveBriefMd: string;
  sections: BrandPlanSection[];
  metrics: BrandPlanMetrics;
  confidenceFlags: BrandPlanConfidenceFlag[];
  provenance: BrandPlanProvenance[];
  /**
   * The committed strategic choices the twelve sections were written against;
   * absent when the spine call failed (`metrics.chainSpineOk === false`).
   * Deliberately opaque here — it is a deep server-side structure (stages,
   * objectives, segments, imperatives, allocation lines) that no client surface
   * reads: `metrics.chainState` and {@link BrandPlanChainAudit} are what a plan
   * renders. Carried rather than dropped so the field is not silently lost.
   */
  spine?: unknown;
  chainAudit: BrandPlanChainAudit;
}

// ── Run handle + stream vocabulary ─────────────────────────

export type BrandPlanPhase = 'preliminary' | 'generate';

/**
 * What a 202 hands back. The work has NOT finished — it has been ACCEPTED and now
 * lives server-side. The next move is to watch `streamUrl` (which is the ordinary
 * reconnect route, so {@link streamAgentReconnect} is the client for it), not to
 * wait on this request.
 */
export interface BrandPlanRunHandle {
  runId: string;
  conversationId: string;
  phase: BrandPlanPhase;
  status: 'running';
  /** `/agent/conversations/:id/live` — handed over so no caller hardcodes the path. */
  streamUrl: string;
}

/**
 * A step the phase will really take, announced up front on `brand_plan_started`.
 * This is what an HONEST progress display is built from: a named checklist that
 * ticks off on real completions (7 buckets, 1 spine hop, 12 sections), never a bar
 * interpolating against a guessed duration.
 */
export interface BrandPlanStepPlan {
  key: string;
  label: string;
  total: number;
}

/**
 * Why a run has no committed spine — the server's `SpineFailureReason`, mirrored as a
 * CLOSED union rather than a bare `string`.
 *
 * It has to be closed because each cause has a different remedy and one of them is not a
 * fault at all: `disabled` means an operator switched the chain off on purpose, so telling
 * that reader to "re-run once the engine is reachable" is wrong. Typed as `string` the
 * remedy table could not be indexed and the copy was unreachable — the UI fell back to one
 * generic sentence for five different situations. See `SPINE_FAILURE_REMEDY` in
 * `@os/lib/brandPlan`, which this union is what makes exhaustive.
 */
export type BrandPlanSpineFailureReason =
  | 'disabled'
  | 'unreachable'
  | 'truncated'
  | 'unparseable'
  | 'incomplete';

/** Emitted at the pipeline's REAL boundaries — a bucket resolves, the spine commits,
 *  a section lands. Every one of these is a fact that happened. */
export type BrandPlanProgressEvent =
  | { step: 'bucket'; key: string; ok: boolean; done: number; total: number }
  | { step: 'spine'; status: 'running' }
  | { step: 'spine'; status: 'done'; ok: boolean; reason?: BrandPlanSpineFailureReason }
  | { step: 'section'; key: string; title: string; done: number; total: number };

/**
 * Everything a brand-plan run emits, replayed on the conversation's reconnect
 * stream. `brand_plan_`-prefixed so it can never be confused with the chat stream's
 * own events on that shared route.
 *
 * `creditsCharged` on `brand_plan_ready` is what was ACTUALLY charged — 0 when the
 * ledger did not move — so a surface can state the cost instead of assuming it.
 */
export type BrandPlanRunEvent =
  | {
      type: 'brand_plan_started';
      data: { runId: string; phase: BrandPlanPhase; steps: BrandPlanStepPlan[] };
    }
  | {
      type: 'brand_plan_progress';
      data: { runId: string; phase: BrandPlanPhase } & BrandPlanProgressEvent;
    }
  | {
      type: 'brand_plan_preliminary_ready';
      data: { runId: string; research: BrandPlanResearch; questions: BrandPlanQuestion[] };
    }
  | {
      type: 'brand_plan_ready';
      data: {
        runId: string;
        plan: BrandPlanOutput;
        creditsCharged: number;
        /** See {@link BrandPlanDeliverableOutcome} — only `queued` may be shown as "a document is coming". */
        deliverable: BrandPlanDeliverableOutcome;
      };
    }
  | {
      type: 'brand_plan_failed';
      data: { runId: string; phase: BrandPlanPhase; message: string; creditsCharged: 0 };
    };

/**
 * What became of the plan's exportable document, as the SERVER reports it.
 *
 * The UI may promise a file under Livrables ONLY on `queued`. `unavailable` means this
 * deployment has no render pipeline wired, so no artifact row was ever created and no
 * card will ever appear — promising one there is a promise the server knows it did not
 * keep. Absent on an older server payload, hence the optional read at the call site.
 */
export type BrandPlanDeliverableOutcome =
  | 'queued' | 'failed' | 'unavailable' | 'none';

// ── Request payloads (mirror the server DTOs field for field) ──

/** Body of `POST /agent/brand-plan/preliminary` (`RunPreliminaryDto`). The account id
 *  is never sent — identity comes from the bearer token. */
export interface RunPreliminaryPayload {
  /**
   * The conversation the durable run attaches to. OPTIONAL: the module library lives on
   * the home screen, where no conversation exists yet and the agent mints one only on a
   * chat turn — so Phase 1 opens its own when none is given and returns its id on the
   * handle. Sent, it must belong to the caller.
   */
  conversationId?: string;
  brand: string;
  indication: string;
  markets: string[];
  competitors: string[];
  ownedDomains: string[];
  molecule?: string;
  budgetTier?: 'S' | 'M' | 'L';
  ambition?: string;
}

/** Body of `POST /agent/brand-plan/generate` (`GenerateBrandPlanDto`). */
export interface GenerateBrandPlanPayload {
  conversationId: string;
  /** The Phase 1 output, replayed verbatim — the route is stateless by design. */
  research: BrandPlanResearch;
  answers: BrandPlanAnswers;
}

// ─── Space types ──────────────────────────────────────────

/**
 * The persistent brand context a Space carries.
 *
 * THIS is the working-context default the agent actually receives: the backend
 * renders these fields into the system prompt (`formatSpaceContextBlock`) on
 * every turn. Any surface that pre-fills or phrases a default must read the same
 * fields, so what the operator is shown is what the agent will assume — the two
 * used to diverge, the workspace pre-filling from the account's default
 * `WorkspaceBrand` while the agent silently defaulted to the active Space.
 */
export interface SpaceBrandContext {
  brand?: string | null;
  molecule?: string | null;
  indication?: string | null;
  markets?: string[];
  competitors?: string[];
  /** Reference audiences the operator writes for. */
  personas?: string[];
  ownedDomains?: string[];
  socialAccounts?: string[];
  keywords?: string[];
  keySources?: string[];
  focusNotes?: string | null;
}

/**
 * The WRITE shape of the same brand context (`POST /agent/spaces`,
 * `PATCH /agent/spaces/:id`). Scalars are plain strings here, never `null`: the
 * DTO validates them as strings and the service normalises an empty string to
 * `NULL`, so `''` is how a field is cleared.
 */
export interface SpaceBrandContextInput {
  brand?: string;
  molecule?: string;
  indication?: string;
  markets?: string[];
  competitors?: string[];
  personas?: string[];
  ownedDomains?: string[];
  socialAccounts?: string[];
  keywords?: string[];
  keySources?: string[];
  focusNotes?: string;
}

/**
 * The folder half of a Space, absorbed from the retired `Project` (L1-5): the
 * prose context its conversations share, and the identity chip the rail renders.
 */
export interface SpaceIdentity {
  /** Free-text working context shared by every conversation of the Space. */
  description?: string | null;
  /** Identity colour (hex). Always present on a row the backend returns. */
  color: string;
  icon: string | null;
}

/**
 * The active Space as `/agent/spaces/active` returns it: the row itself, with no
 * conversation links (unlike {@link SpaceDetail}, which is the detail shape).
 */
export interface ActiveSpace extends SpaceBrandContext, SpaceIdentity {
  id: string;
  userId?: string;
  name: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * A Space as the LIST returns it: the row plus how many conversations are filed
 * in it. The list carries a count, not the links — the rail and the list page
 * render a name, a chip and a count.
 */
export interface Space extends SpaceBrandContext, SpaceIdentity {
  id: string;
  userId?: string;
  name: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { conversations: number };
}

/** A Space with the conversations filed in it — the detail shape. */
export interface SpaceDetail extends Space {
  conversations: {
    conversationId: string;
    addedAt: string;
    conversation: {
      id: string;
      title: string | null;
      agent: 'AIKKA' | 'MEDICAL' | 'MARKETING';
      updatedAt: string;
      messages?: { content: string; role: string; createdAt: string }[];
    };
  }[];
}

/**
 * What `POST /agent/spaces/suggest` answers for a brand name: an openFDA label
 * leg (molecule / manufacturer / indication) and a web-search leg for the
 * competitive set, each degrading on its own.
 *
 * `partial` is the honesty flag and the reason this is a PROPOSAL, never an
 * auto-fill: `competitors: false` means the search could not run at all, which
 * is a different fact from "the search found nobody". A surface that showed an
 * empty list for both would be claiming a result it never obtained.
 */
export interface SpaceSuggestion {
  brand: string;
  market: string;
  molecule: string;
  manufacturer: string;
  indication: string;
  competitors: string[];
  keywords: string[];
  sources: { url: string; title: string }[];
  partial: { openFda: boolean; competitors: boolean };
}

/** One archetype of the persona bank: a real label and its segment share (0–1). */
export interface PersonaArchetypeEntry {
  id: string;
  name: string;
  prevalence: number;
}

/**
 * The persona BANK of a therapeutic area (`GET /agent/personas/archetypes`) —
 * the versioned archetype prior `generate_persona` grounds on, exposed read-only
 * so a Space can be filled from the SAME referential the agent matches against.
 *
 * `available: false` means the prior could not be READ, which is not the same
 * fact as an empty bank (`available: true` with no archetypes) — the latter is
 * an area the versioned referential does not cover: the back-end answers it
 * empty, with `source: null`, rather than handing over the cross-area generic
 * fallback dressed as that area's archetypes with an audience share attached.
 * The picker says which one happened; it never fabricates an archetype to fill
 * the panel, and never states a segment share for an area nothing was derived
 * for.
 */
export interface PersonaBank {
  ta: string;
  source: string | null;
  archetypes: PersonaArchetypeEntry[];
  available: boolean;
}

/** Everything writable on a Space besides its brand context. */
export interface SpaceIdentityInput {
  name?: string;
  description?: string | null;
  color?: string;
  icon?: string | null;
}

export interface AgentFileRecord {
  id: string;
  fileType: string;
  fileName: string;
  s3Url: string | null;
  sizeBytes: number | null;
  mimeType: string | null;
  agent: string | null;
  conversationId: string | null;
  createdAt: string;
}

/** Matches store-back paginated list responses (default limit 20, max 100). */
export interface PaginatedResult<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

const DEFAULT_AGENT_LIST_LIMIT = 100;

// ─── Preferences (per user) ──────────────────────────────

export interface ModelOption {
  id: string;
  name: string;
  role: string;
  provisioned: boolean;
}

/**
 * "Personnaliser" custom instructions (claude.ai parity) — the free-text
 * preferences the agent applies to every answer. This is the server contract;
 * the values are stored per user and injected into the agent's system prompt.
 */
export interface CustomInstructions {
  /** How the assistant should address the operator (name / nickname). */
  displayName: string;
  /** What the assistant should know about the operator and their work. */
  about: string;
  /** How the assistant should respond — tone, format, constraints. */
  style: string;
}

/**
 * Privacy toggles ("Confidentialité", claude.ai parity). Both default OFF — the
 * privacy-preserving choice. Persisted per user and read/written through the same
 * /agent/preferences endpoint as the model picker and custom instructions.
 */
export interface PrivacyPreferences {
  /** Whether the agent may attach coarse location metadata. */
  locationMetadata: boolean;
  /** Whether the operator opts in to model-improvement. */
  aiTrainingOptin: boolean;
}

/**
 * System context ("aide l'IA", Général → Profil) — brand-neutral free-text facts
 * that help the agent situate its answers. Persisted per user and injected into
 * the agent's system prompt (bounded/fenced/after the guardrails), the SAME source
 * of truth (/agent/preferences) as the custom instructions + work description.
 */
export interface SystemContext {
  /** The operator's role / title (e.g. "Directeur marketing"). */
  role: string;
  /** The operator's company / organisation. */
  company: string;
  /** What the operator is working on (domains / focus). */
  focus: string;
}

/** Mode d'accès aux outils (Capacités → Général). Mirrors the server allow-list. */
export type ToolAccessMode = 'auto' | 'always';

/**
 * Capacités (Paramètres → Capacités, claude.ai parity) — the per-user capability
 * preferences the settings surface reads and writes. These PERSIST honestly; the
 * memory / connector-search behaviours they name are not yet built, so nothing
 * consumes them at runtime yet (surfaced + saved so the choice is not lost).
 */
export interface Capabilities {
  /** Mémoire — search & reference this account's chats. */
  searchReferenceConversations: boolean;
  /** Mémoire — generate memory from chat history. */
  generateMemory: boolean;
  /** Général — how the agent loads tools. */
  toolAccessMode: ToolAccessMode;
  /** Général — let the agent search connected sources. */
  connectorSearch: boolean;
}

export interface AgentPreferences {
  /** Selected drafting model id, or null when using the agent default. */
  activeModel: string | null;
  models: ModelOption[];
  /**
   * The user's custom instructions. Optional so older/mocked responses that omit
   * it still type-check; absent ⇒ treat as the empty triplet.
   */
  customInstructions?: CustomInstructions;
  /** The user's privacy toggles. Optional for the same back-compat reason. */
  privacy?: PrivacyPreferences;
  /**
   * The user's Capacités. Optional so older/mocked responses that omit it still
   * type-check; absent ⇒ the client falls back to the shared defaults.
   */
  capabilities?: Capabilities;
  /**
   * "Crédits d'utilisation" (overage opt-in). The stored CHOICE only; absent ⇒
   * OFF. Billing the overage is a separate product contract (not wired yet).
   */
  usageCredits?: { enabled: boolean };
  /**
   * "Meilleure description de votre travail ?" — one of `workDescriptionOptions`,
   * or null when unanswered. Optional so older/mocked responses still type-check.
   */
  workDescription?: string | null;
  /** The fixed, brand-neutral job-family ids the dropdown offers. */
  workDescriptionOptions?: string[];
  /**
   * System context ("aide l'IA") — role / company / focus. Optional so older/mocked
   * responses that omit it still type-check; absent ⇒ treat as the empty triplet.
   */
  systemContext?: SystemContext;
  /**
   * First-run onboarding flag. True once the intro flow was completed OR skipped,
   * so it is shown only once. Absent ⇒ treat as not yet onboarded (false).
   */
  onboardingCompleted?: boolean;
  /**
   * UI theme ("clair" / "sombre" / "système", DESIGN.md §7). Persisted per
   * ACCOUNT — not in browser storage — so the choice follows the operator from
   * one machine to the next. Absent ⇒ the client falls back to its boot cache,
   * then to the OS preference.
   */
  theme?: ThemeChoice;
}

/**
 * DESIGN.md §7 defines TWO themes — "Clair par défaut, sombre en option." — and
 * that is still true: `'system'` is not a third skin, it is the rule that names
 * one of the two from `prefers-color-scheme`. It has to be storable, because
 * without it the operator can leave the OS's choice but never go back to it
 * (docs/dark-theme-scope.md §5, §7).
 */
export type ThemeChoice = 'light' | 'dark' | 'system';

// ─── Shares (owner-side management of public conversation links) ──────────

/**
 * Who may open a share link (mirrors store-back `ShareScope`):
 *  - `link` : anyone holding the URL (anonymous OK) — the historical behaviour.
 *  - `team` : only members of the owner's account.
 *  - `org`  : only the owner's organisation (same account or same email domain).
 */
export type ShareScope = 'link' | 'team' | 'org';

/**
 * One public share link the caller owns (server `OwnerShareView`). The
 * conversation was frozen into a public read-only page at `url` (`/s/:shareId`);
 * `active` is false once revoked or expired. `visibility` is the access scope.
 */
export interface OwnerShare {
  shareId: string;
  conversationId: string;
  title: string;
  url: string;
  visibility: ShareScope;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  viewCount: number;
  active: boolean;
}

/** Body for POST /agent/share — freeze a conversation into a public link. */
export interface CreateSharePayload {
  conversationId: string;
  /** Override the public title (defaults to the conversation title server-side). */
  title?: string;
  /** Access scope; defaults to `link` server-side. */
  scope?: ShareScope;
  /** Lifetime in days; clamped server-side. */
  expiresInDays?: number;
}

// ─── REST API ────────────────────────────────────────────

export interface DemoChatResponse {
  content: string;
  remaining: number;
  quotaReached: boolean;
}

export interface DemoHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

export const agentApi = {
  listAgents: () => apiClient.get<Agent[]>('/agent/agents'),


  // Public playground (no auth) — landing page try-before-signup.
  demoChat: (data: { message: string; sessionId?: string; history?: DemoHistoryItem[] }) =>
    apiClient.post<DemoChatResponse>('/agent/demo', data),

  demoQuota: (sessionId?: string) =>
    apiClient.get<{ remaining: number }>('/agent/demo/quota', {
      params: sessionId ? { sessionId } : {},
    }),

  getAgent: (agentId: string) => apiClient.get<Agent>(`/agent/agents/${agentId}`),

  /**
   * @param opts.archived liste LES ARCHIVÉES à la place des actives. Absent ou
   *        `false` : le comportement d'avant, les actives seules. Archiver
   *        était un aller simple — la conversation quittait la liste et aucune
   *        surface ne pouvait la rouvrir ; c'est ce paramètre qui rend les
   *        archives atteignables (voir `/chats`).
   */
  listConversations: (
    agent?: string,
    search?: string,
    opts?: { page?: number; limit?: number; archived?: boolean },
  ) =>
    apiClient.get<PaginatedResult<ConversationPreview>>('/agent/conversations', {
      params: {
        ...(agent ? { agent } : {}),
        ...(search ? { search } : {}),
        ...(opts?.archived ? { archived: 'true' } : {}),
        page: opts?.page ?? 1,
        limit: opts?.limit ?? DEFAULT_AGENT_LIST_LIMIT,
      },
    }),

  getConversation: (id: string) =>
    apiClient.get<Conversation>(`/agent/conversations/${id}`),

  // Recompute-on-load (BUG #3): the insights/sources panel cards, reconstructed
  // server-side from a reopened conversation's persisted messages. Lets the
  // workspace re-hydrate the panel (and re-light its top-bar toggle) without any
  // card ever having been persisted. Raw cards — the OS stream adapter's
  // `normalizeInsight` shapes them, exactly as it does the live `insight` events.
  getConversationInsights: (id: string) =>
    apiClient.get<{ insights: unknown[] }>(`/agent/conversations/${id}/insights`),

  updateConversation: (
    id: string,
    data: { title?: string; isArchived?: boolean; isPinned?: boolean },
  ) => apiClient.patch(`/agent/conversations/${id}`, data),

  // Read-state: mark a conversation read (clears the orange unread dot). Fired
  // when the user opens the conversation.
  markConversationRead: (id: string) =>
    apiClient.patch<{ id: string; lastReadAt: string | null }>(
      `/agent/conversations/${id}/read`,
    ),

  deleteConversation: (id: string) => apiClient.delete(`/agent/conversations/${id}`),

  // Native rating of one assistant answer. PUT, because there is at most one
  // rating per message: sending the same thumb twice leaves the same single row.
  // The server copies the answer's `runId` onto it, so the opinion is joinable to
  // the prompt and the tool calls that produced it.
  rateMessage: (
    conversationId: string,
    messageId: string,
    data: { rating: MessageRating; comment?: string },
  ) =>
    apiClient.put<MessageFeedback & { messageId: string; conversationId: string }>(
      `/agent/conversations/${conversationId}/messages/${messageId}/feedback`,
      data,
    ),

  // Withdraw a rating (clicking the lit thumb again). Idempotent server-side:
  // "no opinion" is an absent row, never a third rating value.
  clearMessageFeedback: (conversationId: string, messageId: string) =>
    apiClient.delete<{ cleared: true }>(
      `/agent/conversations/${conversationId}/messages/${messageId}/feedback`,
    ),

  chat: (agentId: string, data: { message: string; conversationId?: string }) =>
    apiClient.post(`/agent/${agentId}/chat`, data),

  // Voice input — transcribe a recorded audio clip to text (multipart).
  transcribe: (audio: Blob, filename = 'audio.webm') => {
    const form = new FormData();
    form.append('audio', audio, filename);
    return apiClient.post<{ text: string }>('/agent/transcribe', form);
  },

  // Attach — upload a file; the backend extracts its text for agent context.
  uploadFile: (file: File, conversationId?: string) => {
    const form = new FormData();
    form.append('file', file, file.name);
    if (conversationId) form.append('conversationId', conversationId);
    return apiClient.post<UploadedFile>('/agent/files/upload', form);
  },

  fileUrl: (id: string) => apiClient.get<{ url: string }>(`/agent/files/${id}/url`),

  // Preferences (per user)
  getPreferences: () => apiClient.get<AgentPreferences>('/agent/preferences'),

  // Every slice is optional and independent: send only the one you're changing
  // (a model choice, the custom instructions, a privacy toggle, a capabilities
  // toggle, or the work description) — the others are left untouched server-side.
  // Within `privacy` / `capabilities`, each toggle is independent too, so a single
  // flipped switch PUTs only itself.
  updatePreferences: (data: {
    activeModel?: string | null;
    customInstructions?: CustomInstructions;
    privacy?: Partial<PrivacyPreferences>;
    capabilities?: Partial<Capabilities>;
    usageCredits?: { enabled?: boolean };
    workDescription?: string | null;
    systemContext?: Partial<SystemContext>;
    onboardingCompleted?: boolean;
    theme?: ThemeChoice;
  }) => apiClient.put<AgentPreferences>('/agent/preferences', data),

  // Self-service data export ("Exporter les données"). Returns a JSON blob the
  // caller downloads; every row is scoped to the user server-side.
  exportData: () => apiClient.get<Blob>('/agent/export', { responseType: 'blob' }),

  // Shares — freeze a conversation into a public link (owner-side).
  share: (data: CreateSharePayload) => apiClient.post<OwnerShare>('/agent/share', data),

  // Shares — the caller's public conversation links (list + revoke).
  listShares: () => apiClient.get<{ shares: OwnerShare[] }>('/agent/shares'),

  revokeShare: (shareId: string) =>
    apiClient.delete<{ revoked: true }>(`/agent/share/${encodeURIComponent(shareId)}`),

  getCredits: () => apiClient.get<CreditBalance>('/agent/credits'),

  getUsage: () => apiClient.get<UsageSummary>('/agent/usage'),

  getCreditHistory: () => apiClient.get('/agent/credits/history'),

  // ── Brand plan (MKT-03), the three-phase method ─────────────────────────
  //
  // Both long routes return 202 with a {@link BrandPlanRunHandle}, in
  // milliseconds — the work then runs server-side as a durable run and reports on
  // the conversation's reconnect stream ({@link streamAgentReconnect}). That is
  // what makes them callable at all from here: this axios client aborts at 5s and
  // each phase is minutes of chained model calls.

  /** The deterministic twelve-section outline. No model call, no credits. */
  getBrandPlanSections: () =>
    apiClient.get<{ sections: BrandPlanSectionSpec[] }>('/agent/brand-plan/sections'),

  /**
   * Phase 1 — start the web-grounded evidence sweep and the guided questions built
   * from it. FREE by deliberate server contract: an operator who walks away at the
   * questions is never charged.
   *
   * Resolves as soon as the run is accepted. The research and questions arrive as
   * `brand_plan_preliminary_ready` on the conversation stream, and the caller must
   * KEEP that `research` object — Phase 3 replays it.
   *
   * Rejects 400 (payload out of bounds), 403/404 (not the caller's conversation),
   * 409 (a run is already in flight on this conversation).
   */
  startBrandPlanPreliminary: (data: RunPreliminaryPayload) =>
    apiClient.post<BrandPlanRunHandle>('/agent/brand-plan/preliminary', data),

  /**
   * Phase 3 — start the compilation (spine + twelve sections) from the Phase 1
   * research and the operator's answers.
   *
   * METERED, and charged only after a successful generation. Two different numbers,
   * and a caller that merges them will misinform someone:
   *   · the CHARGE is what the run really cost at the gateway, divided by the credit
   *     anchor — unknown until it finishes, and announced beforehand only as the
   *     fourchette `BRAND_PLAN_COST_RANGE` (55…291 today), which is an estimate;
   *   · the HOLD is `BRAND_PLAN_COST` (150), reserved before any model call and
   *     inside this request, so an insufficient balance rejects here with 403 in
   *     milliseconds rather than after minutes of work. The unused part of the hold
   *     goes back to the balance when the real charge settles.
   * A caller must render that 403 as "you are short N credits against the 150 held,
   * here is what to do", never as a generic error and never as a price.
   *
   * Also rejects 400 / 403 / 404 / 409 exactly as Phase 1 does. On success the plan
   * arrives as `brand_plan_ready` on the conversation stream, carrying the sections,
   * the metrics, the provenance and the confidence flags that must be rendered with it.
   */
  startBrandPlanGeneration: (data: GenerateBrandPlanPayload) =>
    apiClient.post<BrandPlanRunHandle>('/agent/brand-plan/generate', data),

  // Files
  listFiles: (opts?: { page?: number; limit?: number }) =>
    apiClient.get<PaginatedResult<AgentFileRecord>>('/agent/files', {
      params: {
        page: opts?.page ?? 1,
        limit: opts?.limit ?? DEFAULT_AGENT_LIST_LIMIT,
      },
    }),

  // Spaces
  listSpaces: () => apiClient.get<Space[]>('/agent/spaces'),

  /**
   * The user's active Space, or `null`. This is the SAME row the backend injects
   * as the working-context default on every turn, so a surface that pre-fills or
   * phrases a default should read it here rather than guessing from elsewhere.
   */
  getActiveSpace: () => apiClient.get<ActiveSpace | null>('/agent/spaces/active'),

  getSpace: (id: string) => apiClient.get<SpaceDetail>(`/agent/spaces/${id}`),

  /**
   * `GET /agent/spaces/conversation/:id` — the single Space a conversation is
   * filed in, or `null`. Backs the rail's "Changer d'espace / Retirer de
   * l'espace" actions, which need to know where the thread currently lives.
   */
  getConversationSpace: (conversationId: string) =>
    apiClient.get<Space | null>(`/agent/spaces/conversation/${conversationId}`),

  /**
   * Creates a Space, optionally with its brand context in the SAME call (the
   * onboarding "Votre espace" step writes once, at finalisation). The backend
   * makes it the ACTIVE Space when the user has none — an inactive Space reaches
   * no prompt.
   */
  createSpace: (
    name: string,
    icon?: string,
    context?: SpaceBrandContextInput & Omit<SpaceIdentityInput, 'name' | 'icon'>,
  ) => apiClient.post<Space>('/agent/spaces', { name, icon, ...(context ?? {}) }),

  updateSpace: (id: string, data: SpaceIdentityInput & SpaceBrandContextInput) =>
    apiClient.patch<Space>(`/agent/spaces/${id}`, data),

  /**
   * Makes a Space the ACTIVE one — the single row `formatSpaceContextBlock`
   * renders into the prompt. Used when the panel ADOPTS an existing but inactive
   * Space (an account can end up with rows and no active one): editing it would
   * otherwise be an edit the agent never sees.
   */
  activateSpace: (id: string) => apiClient.post<Space>(`/agent/spaces/${id}/activate`),

  deleteSpace: (id: string) => apiClient.delete(`/agent/spaces/${id}`),

  /**
   * Files (MOVES) a conversation into a Space. A conversation lives in at most
   * one Space, so this pulls it out of any other one — the backend enforces it.
   */
  addConversationToSpace: (spaceId: string, conversationId: string) =>
    apiClient.post(`/agent/spaces/${spaceId}/conversations`, { conversationId }),

  removeConversationFromSpace: (spaceId: string, convId: string) =>
    apiClient.delete(`/agent/spaces/${spaceId}/conversations/${convId}`),

  /**
   * Auto-framing from a brand name: an openFDA label read plus a regulatory web
   * search for the competitive set. Fired on an explicit operator click only —
   * it costs an upstream call, and its answer is a PROPOSAL to validate, never
   * something the form fills in on its own.
   */
  suggestSpaceContext: (brand: string, market?: string) =>
    apiClient.post<SpaceSuggestion>('/agent/spaces/suggest', {
      brand,
      ...(market ? { market } : {}),
    }),

  /**
   * The persona bank of a therapeutic area — the versioned archetype prior, the
   * destination the first run points at when it stops asking for reference
   * personas on a blank page.
   */
  listPersonaArchetypes: (therapeuticArea: string) =>
    apiClient.get<PersonaBank>('/agent/personas/archetypes', { params: { therapeuticArea } }),
};

// ─── Streaming ───────────────────────────────────────────

/** Retry only an HTTP 401 rejected before the authenticated agent run starts.
 * Never replay an accepted stream, network failure, or server error: that could
 * create a duplicate paid run. Chat and reconnect share the JSON client's one
 * in-flight token refresh, and a stopped request is never replayed afterwards.
 */
async function fetchAgentStream(url: string, init: RequestInit): Promise<Response> {
  const token = getAuthToken();
  const request = (accessToken: string | null) => {
    const headers = new Headers(init.headers);
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    return fetch(url, { ...init, headers });
  };
  const response = await request(token);
  if (response.status !== 401) return response;
  init.signal?.throwIfAborted();
  const refreshed = await refreshRejectedAuthToken(token);
  init.signal?.throwIfAborted();
  return request(refreshed);
}

/**
 * Posts a chat turn and streams the answer back.
 *
 * The request body carries the message, the conversation, the attachments and —
 * when the turn came from a module wizard rather than the composer — the skill
 * that wizard means. There is no `context`: the context bar is gone (brand /
 * indication / market / audience come from the user's profile, their active Brand
 * Space, as a DEFAULT the request itself overrides in plain language) and so is
 * the Web / GEO / SEO toggle (live grounding is always on, and the agent picks
 * its own tool family from what is being asked).
 *
 * `skillId` is the fix for a real defect class: the back end used to pick the
 * skill by matching trigger words inside the compiled prompt, so a module whose
 * framing happened to contain another skill's vocabulary loaded two skills and
 * held two reservations. A wizard already knows which skill it is; it now says
 * so. Omitted on a free-text turn, where trigger matching is still the right
 * answer, and dropped from the body by `JSON.stringify` when undefined.
 */
export function streamAgentChat(
  agentId: string,
  message: string,
  conversationId?: string,
  onEvent?: (event: AgentStreamEvent) => void,
  fileIds?: string[],
  skillId?: string,
  intake?: AgentBriefInput,
): { abort: () => void; done: Promise<void> } {
  const controller = new AbortController();

  const done = (async () => {
    const res = await fetchAgentStream(`${API_BASE_URL}/agent/${agentId}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message, conversationId, fileIds, skillId,
        brief: intake?.brief,
        briefStatuses: intake?.briefStatuses,
        intakeConfirmed: intake?.intakeConfirmed,
      }),
      signal: controller.signal,
      credentials: 'include',
    });

    if (!res.ok) {
      throw await agentErrorFromResponse(res);
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const event = JSON.parse(raw) as AgentStreamEvent;
          onEvent?.(event);
        } catch {
          /* skip malformed SSE JSON */
        }
      }
    }
  })();

  return { abort: () => controller.abort(), done };
}

/**
 * Reconnects to the run in flight on a conversation (conversation-continuity).
 * GETs the durable SSE endpoint, which opens with a `reconnected` marker,
 * replays every event past `lastEventId` off the server ring buffer, then
 * continues live until the run settles — or emits a single `no_active_run`
 * marker and completes when nothing is running.
 *
 * Unlike {@link streamAgentChat} this parses the SSE `id:` line too, so a caller
 * that wants to resume again after its own drop can pass the last id it saw as
 * `lastEventId` and get only the tail it missed. Aborting the returned handle
 * only detaches this client — the run is durable and keeps going server-side.
 */
export function streamAgentReconnect(
  conversationId: string,
  onEvent?: (event: AgentReconnectEvent) => void,
  lastEventId?: number,
): { abort: () => void; done: Promise<void> } {
  const controller = new AbortController();
  const query =
    typeof lastEventId === 'number' && lastEventId > 0 ? `?lastEventId=${lastEventId}` : '';

  const done = (async () => {
    const res = await fetchAgentStream(
      `${API_BASE_URL}/agent/conversations/${conversationId}/live${query}`,
      {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
        },
        signal: controller.signal,
        credentials: 'include',
      },
    );

    if (!res.ok) {
      throw await agentErrorFromResponse(res);
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        // `id:` lines carry the resume offset; the payload is on `data:`. We only
        // need to translate the payload — the id is echoed back to us as the SSE
        // `data.lastEventId` on the `reconnected` marker — so skip everything else.
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const event = JSON.parse(raw) as AgentReconnectEvent;
          onEvent?.(event);
        } catch {
          /* skip malformed SSE JSON */
        }
      }
    }
  })();

  return { abort: () => controller.abort(), done };
}
